import { useState, useEffect } from 'react';
import { Button, Card, FileDrop, Icon } from '@scuf/common';
import useFetch from 'helpers/hooks/useFetch';
import { RMAMANUFATURA } from 'resources';
import { useTranslation } from 'react-i18next';

type OccupationImage = { occupation: string; image: File | Blob | null; imageCode: string; id?: number };

const initializeOccupationImages = (count: number): OccupationImage[] => {
    return Array.from({ length: count }, () => ({ occupation: '', image: null, imageCode: '' }));
};

interface ImagesManufacturingProps {
    rmaCartIds: number[];
}

const ImagesManufacturing: React.FC<ImagesManufacturingProps> = ({ rmaCartIds }) => {
    const [occupationImages, setOccupationImages] = useState<OccupationImage[]>(initializeOccupationImages(4));
    const [filesToSend, setFilesToSend] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const { t } = useTranslation(['rmamanufatura', 'common', 'user']);
    const [loadingDeleteIndex, setLoadingDeleteIndex] = useState<number | null>(null);
    const [fileDropKey, setFileDropKey] = useState(Date.now());

    const [, uploadManufacturingImages] = useFetch<FormData, any>({
        url: RMAMANUFATURA.uploadManufacturingImages,
        method: 'POST',
        onError: 'ErroUploadImage',
        options: { headers: { 'Content-Type': 'multipart/form-data' } }
    });

    const [, getManufacturingImages, loadingImages] = useFetch<{ rmaCartId: number }>({
        url: RMAMANUFATURA.getManufacturingImages,
        method: 'GET',
        onError: 'Erro Get Imagens'
    });

    useEffect(() => {
        if (rmaCartIds.length > 0) {
            handleGetImages(rmaCartIds);
        } else {
            setOccupationImages(initializeOccupationImages(4));
            setFilesToSend([]);
        }
    }, [rmaCartIds]);

    const handleFileDrop = (acceptedFiles: File[]) => {
        const validFiles = acceptedFiles.filter(file => {
            if (!file.type.includes('jpeg') && !file.type.includes('png') && !file.type.includes('jpg')) {
                alert(t("commo:manual.JPEG/PNG"));
                return false;
            }
            return true;
        });
        setFilesToSend(validFiles);
    };

    const handleGetImages = async (rmaCartIds: number[]): Promise<void> => {
        if (rmaCartIds.length === 0) return;
        const rmaCartId = rmaCartIds[0];

        await getManufacturingImages({ rmaCartId }, resp => {
            const imagesData = resp.data || [];
            const updatedOccupationImages = initializeOccupationImages(4);

            imagesData.slice(0, 4).forEach((imageData: any, index: number) => {
                updatedOccupationImages[index] = {
                    occupation: imageData.fileName,
                    image: null,
                    imageCode: `data:${imageData.contentType || 'image/jpeg'};base64,${imageData.imageData}`,
                    id: imageData.id
                };
            });
            setOccupationImages(updatedOccupationImages);
        });
    };

    const handleSubmit = async () => {
        if (filesToSend.length === 0) return;

        setIsUploading(true);

        const formData = new FormData();
        rmaCartIds.forEach(id => formData.append('rmaCartIds', String(id)));
        filesToSend.forEach((file, index) => {
            const parts = file.name.split('.');
            const ext = parts.pop();
            const base = parts.join('.');
            const newName = `${base}_${Date.now()}_${index}.${ext}`;
            formData.append('imageFiles', new File([file], newName, { type: file.type }));
        });

        try {
            await uploadManufacturingImages(formData);
            setFilesToSend([]);
            setFileDropKey(Date.now());
        } catch {
            alert(t("common:manual.uploadError"));
        } finally {
            setIsUploading(false);
            await handleGetImages(rmaCartIds);
        }
    };

    const handleImageRemove = async (index: number) => {
        setLoadingDeleteIndex(index);
        const imageToRemove = occupationImages[index];

        if (imageToRemove?.id) {
            try {
                await fetch(`${RMAMANUFATURA.deleteManufacturingImage}?Id=${imageToRemove.id}`, { method: 'POST' });
                await handleGetImages(rmaCartIds);
            } catch (err) {
                alert(t("common:manual.deleteFileError"));
            } finally {
                setLoadingDeleteIndex(null);
            }
        }
    };

    return (
        <Card>
            <Card.Content>
                <h1>
                    <Icon root="common" name="edit" size="large" style={{ marginRight: '10px', marginBottom: '5px' }} /> {t("common:actions.UploadImageFile")}
                </h1>
                <FileDrop
                    key={fileDropKey}
                    acceptedTypes={['.png', '.jpeg', '.jpg']}
                    resetButtonText={t("common:actions.Clean")}
                    multiple
                    buttonText={t("common:actions.BrowseFiles")}
                    label=""
                    placeholder={t("common:actions.Draganddropfileshere")}
                    onAccepted={handleFileDrop}
                    onReset={() => setFilesToSend([])}
                />
                <div className="form-group" style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                    <Button
                        name="primary"
                        content={t('common:translation.UploadImageFile')}
                        onClick={handleSubmit}
                        disabled={!rmaCartIds.length || filesToSend.length === 0}
                        className="button-upload"
                        loading={isUploading}
                    />
                </div>

                <div className="image-previews">
                    {loadingImages ? (
                        Array.from({ length: 4 }).map((_, index) => (
                            <div key={index} className="image-preview" style={{ position: 'relative', marginBottom: '10px', marginTop: '35px' }}>
                                <div style={{ height: '150px', width: '200px', border: '2px dashed #ccc', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f7f7f7' }}>
                                    <Icon name="refresh" size="large" loading={true} />
                                </div>
                            </div>
                        ))
                    ) : (
                        occupationImages.map((item, index) => (
                            <div key={item.id || index} className="image-preview" style={{ position: 'relative', marginBottom: '10px', marginTop: '35px' }}>
                                {item.imageCode ? (
                                    <>
                                        <img src={item.imageCode} alt={`preview-${index}`} />
                                        <div className="image-actions">
                                            {loadingDeleteIndex === index ? (
                                                <Icon name="refresh" size="medium" loading={true} style={{ paddingRight: '10px', paddingTop: '10px' }} />
                                            ) : (
                                                <Icon name="delete" size="medium" onClick={() => handleImageRemove(index)} style={{ paddingRight: '10px', paddingTop: '10px', cursor: 'pointer', color: 'red', background: 'rgba(0, 0, 0, 0.5)', borderRadius: '50%' }} />
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ height: '150px', width: '200px', border: '2px dashed #ccc', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f7f7f7' }}>
                                        <Icon name="camera" size="large" color="#d3d3d3" />
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </Card.Content>
        </Card>
    );
};

export default ImagesManufacturing;